import { consumeReturnPath } from '../lib/workspaceStorage';
import { clearGoogleHandoff, readGoogleHandoff } from '../lib/googleHandoff';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { roleHome, ORGANIZER_ONBOARDING, organizerNeedsOnboarding } from '../config/brand';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STEPS = ['link_password', 'set_password', 'choose_role'];
const callbackError = code => code === 'cancelled'
  ? 'Google girişi iptal edildi. Giriş ekranından yeniden deneyebilirsin.'
  : code === 'google_failed'
    ? 'Google doğrulaması tamamlanamadı. Giriş ekranından yeniden Google ile devam et.'
    : 'Giriş bağlantısı geçersiz veya süresi dolmuş. Giriş ekranından yeniden Google ile devam et.';

export default function GoogleCallback() {
  const [handoff] = useState(() => readGoogleHandoff());
  const [step, setStep] = useState('loading');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('organizer');
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const submitting = useRef(false);
  const mounted = useRef(false);
  const { acceptGoogleSession } = useAuth();
  const navigate = useNavigate();

  const complete = async (extra = {}) => {
    if (submitting.current) return;
    if (extra.password && new TextEncoder().encode(extra.password).length > 72) {
      setError('MekandaPro şifren en fazla 72 UTF-8 baytı olabilir. Gerekirse şifreni yenile.');
      return;
    }
    submitting.current = true;
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${BACKEND}/api/auth/google/complete`, {
        ticket: handoff.ticket, verifier: handoff.verifier, ...extra,
      }, { timeout: 20000 });
      if (!mounted.current) return;
      if (STEPS.includes(data.step)) {
        setStep(data.step); setEmail(data.email || ''); return;
      }
      if (data.step || !data.access_token || !data.refresh_token || !data.user) throw new Error('invalid response');
      acceptGoogleSession(data);
      clearGoogleHandoff();
      setPassword('');
      let target = roleHome(data.user.role);
      if (data.user.role === 'organizer') {
        try {
          const profile = await axios.get(`${BACKEND}/api/organizers/me`, { timeout: 15000 });
          if (organizerNeedsOnboarding(profile.data)) target = ORGANIZER_ONBOARDING;
        } catch { target = ORGANIZER_ONBOARDING; }
      }
      if (data.user.role === 'venue') {
        try {
          const profile = await axios.get(`${BACKEND}/api/venues/me`, { timeout: 15000 });
          if (!profile.data) target = '/venue/profile';
        } catch { target = '/venue/profile'; }
      }
      if (mounted.current) navigate([ORGANIZER_ONBOARDING, '/venue/profile'].includes(target)
        ? target : consumeReturnPath() || target, { replace: true });
    } catch (err) {
      if (!mounted.current) return;
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : !err.response
        ? 'Bağlantı kesildi veya yanıt süresi doldu. Sonuç doğrulanamadı; giriş ekranından yeniden Google ile devam et.'
        : 'Google girişi tamamlanamadı. Giriş ekranından yeniden başlayabilirsin.');
      // Only a rejected password can safely reuse this handoff. An ambiguous timeout
      // may have consumed it, so do not automatically resend or claim completion.
      if (!(status === 401 && step === 'link_password')) {
        clearGoogleHandoff(); setPassword(''); setStep('error');
      }
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    if (!started.current) {
      started.current = true;
      window.history.replaceState(null, '', window.location.pathname);
      if (handoff.error) { setError(callbackError(handoff.error)); setStep('error'); }
      else complete();
    }
    return () => { mounted.current = false; };
    // One request per mounted callback, including StrictMode effect replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recoveryLink = <Link className="block text-sm text-[#1E3A8A] underline" to="/forgot-password"
    state={{ email, googleLink: true }} onClick={clearGoogleHandoff}>
    {step === 'set_password' ? 'E-postayla şifre oluştur' : 'Şifremi unuttum'}
  </Link>;

  return <main className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6 py-10">
    <section className="w-full max-w-sm min-w-0 rounded-2xl border border-zinc-100 bg-white p-6 shadow-sm">
      <h1 className="text-xl font-semibold">Google ile devam et</h1>
      {step === 'loading' && <p role="status" className="mt-4 text-sm">Giriş tamamlanıyor…</p>}
      {step === 'link_password' && <form className="mt-4 space-y-4" onSubmit={e => { e.preventDefault(); complete({ password }); }}>
        <p className="text-sm text-zinc-600">Bu e-postayla bir MekandaPro hesabın var. Hesabını bağlamak için mevcut MekandaPro şifreni bir kez doğrula. Google şifreni istemiyoruz.</p>
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          <label className="block text-sm">Mevcut MekandaPro şifren
            <input type="password" autoComplete="current-password" required maxLength={128} value={password} onChange={e => setPassword(e.target.value)} className="mt-2 w-full rounded-lg border p-3" />
          </label>
          <button type="submit" disabled={busy} className="w-full rounded-lg bg-[#1E3A8A] p-3 text-white disabled:opacity-50">{busy ? 'Kontrol ediliyor…' : 'Hesabımı Google’a bağla'}</button>
        </fieldset>
        {recoveryLink}
      </form>}
      {step === 'set_password' && <div className="mt-4 space-y-4">
        <p className="text-sm text-zinc-600">Mevcut hesabında kullanılabilir bir MekandaPro şifresi bulunmuyor. E-posta adresine gönderilen bağlantıyla şifre oluştur; ardından Google ile devam et.</p>
        <p className="break-all text-sm font-medium">{email}</p>
        {recoveryLink}
      </div>}
      {step === 'choose_role' && <form className="mt-4 space-y-4" onSubmit={e => { e.preventDefault(); complete({ role }); }}>
        <p className="text-sm">MekandaPro’yu nasıl kullanacaksınız?</p>
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          <legend className="sr-only">Hesap türü</legend>
          <label className="block text-sm"><input type="radio" name="role" checked={role === 'organizer'} onChange={() => setRole('organizer')} /> Etkinlik düzenlemek istiyorum</label>
          <label className="block text-sm"><input type="radio" name="role" checked={role === 'venue'} onChange={() => setRole('venue')} /> Mekân sahibiyim / işletiyorum</label>
          <button disabled={busy} className="w-full rounded-lg bg-[#1E3A8A] p-3 text-white disabled:opacity-50">{busy ? 'Hesap oluşturuluyor…' : 'Hesabımı oluştur'}</button>
        </fieldset>
      </form>}
      {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
      <Link to="/login" onClick={clearGoogleHandoff} className="mt-6 block text-center text-sm text-[#1E3A8A]">
        {step === 'error' ? 'Giriş ekranına dön ve Google ile yeniden başla' : 'Giriş ekranına dön'}
      </Link>
    </section>
  </main>;
}
